import React, { useRef } from "react";
import { useRouter } from "next/router";
import { ExpandedFilters } from "./components/ExpandedFilters";
import { FooterButtons } from "./components/FooterButtons";
import { MainSelects } from "./components/MainSelects";
import { useCompareInitialization } from "./hooks/useCompareInitialization";
import { useFilterHandlers } from "./hooks/useFilterHandlers";
import { useFilterOptions } from "./hooks/useFilterOptions";
import { useFormattedOptions } from "./hooks/useFormattedOptions";
import { useMainFilterHelpers } from "./hooks/useMainFilterHelpers";
import { useMainFilterState } from "./hooks/useMainFilterState";
import MainFilterHeader from "./MainFilterHeader";
import { MainFilterProps } from "./types";
import classes from "./MainFilter.module.scss";

const MainFilter: React.FC<MainFilterProps> = ({
    isMainPage = true,
    onFilterSubmit,
    resetFilters,
    previewQnt,
    isPreviewQntFetching,
    updatePreviewQntIsLoading,
    disableMapListMode
}) => {
    const targetExpandedRef = useRef<HTMLDivElement | null>(null);
    const router = useRouter();

    useCompareInitialization();

    const {
        filters,
        compareCount,
        showBadgeCount,
        btnLabel,
        isExpanded,
        setIsExpanded,
        locationState,
        setLocationState,
        targetPath
    } = useMainFilterState(previewQnt);

    const {
        customSelectComponents,
        createSelectValue,
        createGenerationSelectValue,
        selectInputFonts,
        topSelectsFontStyle
    } = useMainFilterHelpers();

    const filterOptions = useFilterOptions(
        filters.brand,
        filters.model || undefined,
        filters.generation || undefined,
        isExpanded
    );

    const handlers = useFilterHandlers(filterOptions.generationsOptions);

    const formattedOptions = useFormattedOptions(
        filterOptions.brandsOptions,
        filterOptions.modelOptions,
        filterOptions.generationsOptions,
        filters.model || undefined,
        filterOptions.modelOptionsIsFetching,
        filterOptions.isFetchingGenerations
    );

    const containerClassName = `${classes.MainFilter} ${
        isExpanded ? classes.ExpandedMode : ""
    } ${isMainPage ? classes.MainPage : classes.InnerPage}`;

    return (
        <div className={containerClassName}>
            <MainFilterHeader
                isLocationFilterShown={locationState.isFilterShown}
                setIsLocationFilterShown={(value) =>
                    setLocationState((prev) => ({ ...prev, isFilterShown: value }))
                }
                isLocationConfirmShown={locationState.isConfirmShown}
                setIsLocationConfirmShown={(value) =>
                    setLocationState((prev) => ({ ...prev, isConfirmShown: value }))
                }
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                targetExpandedRef={targetExpandedRef}
            />

            <div className={classes.Inputs}>
                <MainSelects
                    brandValue={filters.brand}
                    modelValue={filters.model || undefined}
                    generationValue={filters.generation || undefined}
                    yearFromValue={filters.yearFrom}
                    yearToValue={filters.yearTo}
                    mileageFromValue={filters.mileageFrom}
                    mileageToValue={filters.mileageTo}
                    priceFromValue={filters.priceFrom}
                    priceToValue={filters.priceTo}
                    sourceId={filters.sourceId}
                    countryValue={filters.country}
                    brandsOptionsFormatted={formattedOptions.brandsOptionsFormatted}
                    modelFormattedOptions={formattedOptions.modelFormattedOptions}
                    generationFormattedOptions={formattedOptions.generationFormattedOptions}
                    filterOptionsLoading={{
                        brandsOptionsIsFetching: filterOptions.brandsOptionsIsFetching,
                        modelOptionsIsFetching: filterOptions.modelOptionsIsFetching,
                        isFetchingGenerations: filterOptions.isFetchingGenerations
                    }}
                    generationsOptions={filterOptions.generationsOptions}
                    customSelectComponents={customSelectComponents}
                    topSelectsFontStyle={topSelectsFontStyle}
                    createSelectValue={createSelectValue}
                    createGenerationSelectValue={createGenerationSelectValue}
                    onBrandChange={handlers.handleBrandChange}
                    onModelChange={handlers.handleModelChange}
                    onGenerationChange={handlers.handleGenerationChange}
                    onYearFromChange={handlers.handleYearFromChange}
                    onYearToChange={handlers.handleYearToChange}
                    onMileageFromChange={handlers.handleMileageFromChange}
                    onMileageToChange={handlers.handleMileageToChange}
                    onPriceFromChange={handlers.handlePriceFromChange}
                    onPriceToChange={handlers.handlePriceToChange}
                    onSourceChange={handlers.handleSourceChange}
                    onCountryChange={handlers.handleCountryChange}
                />

                {isExpanded && (
                    <>
                        <div className={classes.Divider} />
                        <ExpandedFilters
                            filterValues={{
                                bodyValue: filters.body,
                                transmissionValue: filters.transmission,
                                engineTypeValue: filters.engineType,
                                driveValue: filters.drive,
                                engineVolumeFromValue: filters.engineVolumeFrom,
                                engineVolumeToValue: filters.engineVolumeTo,
                                horsePowerFromValue: filters.horsePowerFrom,
                                horsePowerToValue: filters.horsePowerTo,
                                wheelValue: filters.wheel,
                                ownerQntFrom: filters.ownersQntFrom,
                                ownersQntTo: filters.ownersQntTo,
                                sellerType: filters.sellerType,
                                hideNoImages: filters.hideNoImages
                            }}
                            filterOptions={{
                                bodyOptions: filterOptions.bodyOptions,
                                bodyOptionsIsFetching: filterOptions.bodyOptionsIsFetching,
                                transmissionOptions: filterOptions.transmissionOptions,
                                transmissionOptionsIsFetch:
                                    filterOptions.transmissionOptionsIsFetch,
                                engineTypesOptions: filterOptions.engineTypesOptions,
                                engineTypesIsFetching: filterOptions.engineTypesIsFetching,
                                driveTypesOptions: filterOptions.driveTypesOptions,
                                driveTypesIsFetching: filterOptions.driveTypesIsFetching,
                                displacementsOptions: filterOptions.displacementsOptions,
                                displacementsIsFetching: filterOptions.displacementsIsFetching,
                                wheelsOptions: filterOptions.wheelsOptions,
                                wheelsIsFetching: filterOptions.wheelsIsFetching,
                                sellerTypesOptions: filterOptions.sellerTypesOptions,
                                sellerTypesIsFetching: filterOptions.sellerTypesIsFetching
                            }}
                            customSelectComponents={customSelectComponents}
                            selectInputFonts={selectInputFonts}
                            createSelectValue={createSelectValue}
                        />
                        <div className={classes.Divider} />
                    </>
                )}
            </div>

            <div ref={targetExpandedRef}>
                <FooterButtons
                    targetPath={targetPath || "/catalog"}
                    btnLabel={btnLabel}
                    previewQnt={previewQnt}
                    isPreviewQntFetching={isPreviewQntFetching}
                    updatePreviewQntIsLoading={updatePreviewQntIsLoading}
                    compareCount={compareCount}
                    showBadgeCount={showBadgeCount}
                    onFilterSubmit={onFilterSubmit}
                    resetFilters={resetFilters}
                    disableMapListMode={disableMapListMode}
                    onRouterPush={(path) => router.push(path)}
                />
            </div>
        </div>
    );
};

export default MainFilter;